import { ComponentType, lazy } from 'react';

/**
 * Storage key used to track chunk load retry attempts.
 * This prevents infinite reload loops when a chunk truly cannot be loaded.
 */
const CHUNK_RETRY_KEY = 'chunk_load_retry';
// dummy comment-only change so the app build runs (EPD-526)

/**
 * Maximum number of page reloads to attempt when a chunk fails to load.
 * After this many attempts, the error will be thrown to the error boundary.
 */
const MAX_RETRIES = 2;

/**
 * Time window (in ms) to consider retries as being for the same chunk error.
 * If more time passes between attempts, the retry counter resets.
 */
const RETRY_WINDOW_MS = 30000; // 30 seconds

/**
 * Checks if an error is a chunk/module loading error.
 * These errors occur when dynamically imported modules fail to load,
 * typically after a new deployment when old chunk hashes no longer exist.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();
  const errorString = String(error).toLowerCase();

  return (
    // Standard dynamic import failure messages
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    // Vite: a removed CSS file fails in the preload helper (EPD-602)
    message.includes('unable to preload css') ||
    // Network-related chunk loading failures
    message.includes('failed to load module script') ||
    errorString.includes('chunkloaderror') ||
    // Syntax errors from loading wrong/old chunks
    (message.includes('unexpected token') && message.includes('<')) ||
    // Additional patterns for various browsers
    message.includes('importing a module script failed') ||
    message.includes('cannot find module') ||
    // Safari: a removed chunk is served as index.html (EPD-379)
    message.includes('is not a valid javascript mime type') ||
    // Firefox
    message.includes('error loading dynamically imported module')
  );
}

/**
 * Gets the current retry state from sessionStorage.
 */
function getRetryState(): { count: number; timestamp: number } {
  try {
    const stored = sessionStorage.getItem(CHUNK_RETRY_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore storage errors
  }
  return { count: 0, timestamp: 0 };
}

/**
 * Sets the retry state in sessionStorage.
 */
function setRetryState(count: number): void {
  try {
    sessionStorage.setItem(CHUNK_RETRY_KEY, JSON.stringify({ count, timestamp: Date.now() }));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Handles a chunk loading error by potentially triggering a page reload.
 * Returns true if a reload was triggered, false otherwise.
 */
export function handleChunkLoadError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;

  const state = getRetryState();
  const now = Date.now();

  // Reset retry count if we're outside the retry window
  const effectiveCount = now - state.timestamp > RETRY_WINDOW_MS ? 0 : state.count;

  if (effectiveCount < MAX_RETRIES) {
    console.warn(
      `[ChunkLoader] Chunk loading failed (attempt ${effectiveCount + 1}/${MAX_RETRIES}). ` +
        'Reloading page to fetch latest assets...'
    );

    setRetryState(effectiveCount + 1);

    // Force a hard reload to bypass cache
    window.location.reload();
    return true;
  }

  console.error(
    '[ChunkLoader] Max retry attempts reached. Chunk loading error will be shown to user.'
  );
  return false;
}

/**
 * A wrapper around React.lazy that adds retry logic for chunk loading failures.
 *
 * When a dynamic import fails (common after deployments when chunk hashes change),
 * this function will:
 * 1. First attempt: Retry the import immediately (handles transient network errors)
 * 2. Subsequent failures: Trigger a page reload to fetch the latest asset manifest
 *
 * Usage:
 * ```typescript
 * // Instead of:
 * const MyComponent = lazy(() => import('./MyComponent'));
 *
 * // Use:
 * const MyComponent = lazyWithRetry(() => import('./MyComponent'));
 * ```
 */
export function lazyWithRetry<T extends ComponentType<object>>(
  importFunction: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      // First attempt
      return await importFunction();
    } catch (error) {
      // Check if this is a chunk loading error
      if (isChunkLoadError(error)) {
        console.warn('[ChunkLoader] First import attempt failed, retrying...');

        try {
          // Wait a short moment and retry (helps with transient network issues)
          await new Promise(resolve => setTimeout(resolve, 1000));
          return await importFunction();
        } catch (retryError) {
          // Retry failed, handle with page reload
          const reloading = handleChunkLoadError(retryError);

          if (reloading) {
            // Never resolves: page reload was triggered, so the component tree is discarded.
            return new Promise<{ default: T }>(() => {});
          }

          // Max retries exceeded, throw the error for the error boundary
          throw retryError;
        }
      }

      // Not a chunk loading error, throw immediately
      throw error;
    }
  });
}

export default lazyWithRetry;
